/**
 * Staff handbook: the generated tables and per-class figures the guide quotes (STAFF_GUIDE_PLAN.md §3),
 * built from the instruments and categories as stored. The API (staff-guide/service.ts) runs these on
 * the live rows; GUIDE_VAR_DEFAULTS runs them on the catalogue's preset hours, the value used when the
 * live rows can't be read.
 *
 * A table's generated rows travel as one placeholder holding JSON (`string[][]`), named by the table
 * block's `rowsFrom` and appended to its fixed rows when the article is filled.
 */
import { D } from '../decimal.js';
import { fmtUsd } from '../format.js';
import { describeHours } from '../hours.js';
import { HOURS_PRESETS } from '../instrument-search.js';
import { NETWORKS, WITHDRAWAL_ASSETS } from '../account.js';
import { FEATURE_KEYS, FEATURE_NAMES } from '../features.js';
import { FEE_ASSET_CLASSES, STANDARD_FEES } from '../fees.js';
import { PAYOUT_CURRENCIES, WITHDRAWAL_METHODS } from '../payouts.js';
import { PROVIDER_BY_ID, ROUTE_CLASS_LABEL, ROUTE_CLASSES } from '../providers.js';
import { termLabel } from '../yield.js';
/** The asset classes in the order the guide lists them, with their names. */
export const GUIDE_CLASS_LABEL = {
    CRYPTO: 'Crypto',
    FX: 'Forex',
    COMMODITY: 'Commodities',
    INDEX: 'Indices',
    STOCK: 'Stocks',
    MEME: 'Meme coins',
};
const CLASS_ORDER = Object.keys(GUIDE_CLASS_LABEL);
const classLabel = (c) => GUIDE_CLASS_LABEL[c] ?? c;
const byClassOrder = (a, b) => (CLASS_ORDER.indexOf(a) + 1 || 99) - (CLASS_ORDER.indexOf(b) + 1 || 99) || a.localeCompare(b);
/** Rows for a generated table, as the placeholder carries them. */
export const guideRows = (rows) => JSON.stringify(rows);
/** The rows a placeholder holds; [] when it isn't a JSON list of rows. */
export function parseGuideRows(value) {
    if (!value)
        return [];
    try {
        const v = JSON.parse(value);
        return Array.isArray(v) && v.every((r) => Array.isArray(r) && r.every((c) => typeof c === 'string')) ? v : [];
    }
    catch {
        return [];
    }
}
/** The hours in words; a schedule with no sessions never opens. */
const hoursText = (h) => ('sessions' in h && !h.sessions.length ? 'Never open (no sessions set)' : describeHours(h));
/** "BTC, ETH, SOL" or "BTC, ETH, SOL and 41 more". */
function symbolsText(symbols) {
    if (symbols.length <= 4)
        return symbols.join(', ');
    return `${symbols.slice(0, 3).join(', ')} and ${symbols.length - 3} more`;
}
/** The instruments of each class, grouped by their weekly sessions, the commonest schedule first. */
function hoursGroups(list) {
    const classes = [...new Set(list.map((i) => i.assetClass))].sort(byClassOrder);
    return classes.map((c) => {
        const groups = new Map();
        for (const i of list.filter((x) => x.assetClass === c)) {
            const k = JSON.stringify(i.tradingHours ?? { always: true });
            const g = groups.get(k) ?? { hours: i.tradingHours ?? { always: true }, symbols: [] };
            g.symbols.push(i.symbol);
            groups.set(k, g);
        }
        return { assetClass: c, groups: [...groups.values()].sort((a, b) => b.symbols.length - a.symbols.length || a.symbols[0].localeCompare(b.symbols[0])) };
    });
}
/** Market hours: one row per asset class and schedule: [asset class, hours, instruments (in the list's order)]. */
export function guideMarketHoursRows(list) {
    return hoursGroups(list).flatMap(({ assetClass, groups }) => groups.map((g) => [classLabel(assetClass), hoursText(g.hours), symbolsText(g.symbols)]));
}
/** Each class's commonest schedule in words, e.g. `{ FX: 'Sun 17:00 to Fri 17:00 New York time, without a daily break' }`. */
export function guideClassHours(list) {
    return Object.fromEntries(hoursGroups(list).map(({ assetClass, groups }) => [assetClass, hoursText(groups[0].hours)]));
}
/** "$10" when every instrument has the same, else "$10 to $1,000". */
function span(values, show) {
    const sorted = [...values].sort((a, b) => D.from(a).cmp(D.from(b)));
    const lo = sorted[0];
    const hi = sorted.at(-1);
    return D.from(lo).eq(D.from(hi)) ? show(lo) : `${show(lo)} to ${show(hi)}`;
}
const usd = (x) => fmtUsd(x, 2).replace(/\.00$/, '');
/** Order sizes per asset class: [asset class, minimum volume, minimum order, maximum order]. */
export function guideOrderSizeRows(list) {
    const classes = [...new Set(list.map((i) => i.assetClass))].sort(byClassOrder);
    return classes.map((c) => {
        const of = list.filter((i) => i.assetClass === c);
        const lots = (x) => `${D.from(x).toString()} lots`;
        return [classLabel(c), span(of.map((i) => i.minLots), lots), span(of.map((i) => i.minNotional), usd), span(of.map((i) => i.maxNotional), usd)];
    });
}
/** Leverage per category: [category, minimum, default, maximum, instruments]. */
export function guideLeverageRows(categories) {
    return categories.map((c) => [c.name, `1:${c.minLeverage}`, `1:${c.defaultLeverage}`, `1:${c.maxLeverage}`, String(c.instruments)]);
}
const preset = (id) => HOURS_PRESETS.find((p) => p.id === id).hours;
const sample = (symbol, assetClass, hours, minNotional = '10', maxNotional = '250000') => ({
    symbol,
    assetClass,
    tradingHours: preset(hours),
    minLots: '0.01',
    minNotional,
    maxNotional,
});
/** The catalogue's preset hours, for the defaults: what the seeded instruments use. */
export const GUIDE_SAMPLE_INSTRUMENTS = [
    sample('BTC', 'CRYPTO', 'always'),
    sample('EURUSD', 'FX', 'forex', '10', '1000000'),
    sample('GOLD', 'COMMODITY', 'futures'),
    sample('SPX500', 'INDEX', 'futures'),
    sample('DE40', 'INDEX', 'europe'),
    sample('UK100', 'INDEX', 'london'),
    sample('HK50', 'INDEX', 'hongkong'),
    sample('AUS200', 'INDEX', 'sydney'),
    sample('AAPL', 'STOCK', 'nyse'),
];
/** The categories migration 027 seeds on an empty catalogue (min 1, default 20 capped at the max). */
export const GUIDE_SAMPLE_CATEGORIES = [
    { name: 'Crypto', minLeverage: 1, defaultLeverage: 20, maxLeverage: 20, instruments: 0 },
    { name: 'Forex', minLeverage: 1, defaultLeverage: 20, maxLeverage: 100, instruments: 0 },
    { name: 'Commodities', minLeverage: 1, defaultLeverage: 20, maxLeverage: 20, instruments: 0 },
    { name: 'Indices', minLeverage: 1, defaultLeverage: 20, maxLeverage: 50, instruments: 0 },
    { name: 'Stocks', minLeverage: 1, defaultLeverage: 20, maxLeverage: 20, instruments: 0 },
];
/** The placeholders these give, from a list of instruments and categories. */
export function guideMarketVars(list, categories) {
    const hours = guideClassHours(list);
    const none = 'no instrument of this class is enabled';
    return {
        marketHoursRows: guideRows(guideMarketHoursRows(list)),
        orderSizeRows: guideRows(guideOrderSizeRows(list)),
        leverageRows: guideRows(guideLeverageRows(categories)),
        cryptoHours: hours.CRYPTO ?? none,
        fxHours: hours.FX ?? none,
        commodityHours: hours.COMMODITY ?? none,
        indexHours: hours.INDEX ?? none,
        stockHours: hours.STOCK ?? none,
    };
}
// ---------- money, products and settings (Client lifecycle, Products, Admin settings) ----------
/** "$250", "$12.50"; "No limit" for null. */
const usdOrNone = (x) => (x === null ? 'No limit' : usd(x));
/** "0.05%" from a fraction. */
const pctOf = (fraction) => `${D.from(fraction).mul(100).toString()}%`;
/** Withdrawal limits per method (Admin → Withdrawal limits): [method, minimum, most at once, most in 24 hours, fee]. */
export function guideWithdrawRows(l) {
    const crypto = WITHDRAWAL_ASSETS.map((a) => {
        const al = l.crypto.assets[a.id];
        const min = D.max(D.from(al.min), D.from(l.crypto.min)).toString();
        const fee = D.from(al.fee).isZero() ? 'None' : `${D.from(al.fee).toString()} ${a.coin} (network fee)`;
        return [`Crypto: ${a.label}`, usd(min), usdOrNone(l.crypto.max), usdOrNone(l.crypto.daily), fee];
    });
    const fiat = ['bank_wire', 'card'].map((m) => {
        const fees = PAYOUT_CURRENCIES.map((c) => [c, l[m].fees[c]]);
        const fee = fees.every(([, f]) => D.from(f).isZero()) ? 'None' : fees.map(([c, f]) => `${usd(f)} (${c} payout)`).join(' · ');
        return [WITHDRAWAL_METHODS.find((x) => x.id === m).label, usd(l[m].min), usdOrNone(l[m].max), usdOrNone(l[m].daily), fee];
    });
    return [...crypto, ...fiat];
}
/** Trading fees per asset class (Admin → Fees): [asset class, commission per side, flat fee per trade, instruments]. */
export function guideCommissionRows(trading, flat) {
    const shown = (f, fmtv) => {
        if (!f || f.min === null || f.max === null)
            return '—';
        const one = (x) => (D.from(x).isZero() ? 'None' : fmtv(x));
        return f.rate !== null ? one(f.rate) : `${fmtv(f.min)} to ${fmtv(f.max)}`;
    };
    return FEE_ASSET_CLASSES.map((c) => [c.label, shown(trading[c.id], pctOf), shown(flat[c.id], usd), String(trading[c.id]?.instruments ?? 0)]);
}
/** The standard fee preset as the fee table, for the defaults. */
export const guideStandardFeeRows = () => {
    const f = (v) => ({ rate: v ?? '0', min: v ?? '0', max: v ?? '0', instruments: 0 });
    return guideCommissionRows(Object.fromEntries(FEE_ASSET_CLASSES.map((c) => [c.id, f(STANDARD_FEES.trading[c.id])])), Object.fromEntries(FEE_ASSET_CLASSES.map((c) => [c.id, f(STANDARD_FEES.trading_flat[c.id])]))).map((r) => [r[0], r[1], r[2], '—']);
};
/** Crypto deposit networks (address deposits): [network, confirmations needed, smallest deposit credited]. */
export const guideNetworkRows = () => NETWORKS.map((n) => [`USDT on ${n.id} (${n.chain})`, String(n.confirmations), usd(n.minDeposit)]);
/** Product switches (Admin → Features): [feature, for clients, blocked countries]. */
export const guideFeatureRows = (s) => FEATURE_KEYS.map((k) => [FEATURE_NAMES[k], s[k].enabled ? 'On' : 'Off (staff only)', s[k].blockedCountries.join(', ') || 'None']);
/** Price routing (Admin → Data providers): [asset class, providers, first to last]. */
export const guideRoutingRows = (r) => ROUTE_CLASSES.map((c) => [ROUTE_CLASS_LABEL[c], (r[c] ?? []).map((p) => PROVIDER_BY_ID[p]?.label ?? p).join(' → ') || 'None']);
/** The deposit address pool (Money → Deposits): [network, free addresses, assigned]. */
export const guidePoolRows = (pool) => NETWORKS.map((n) => {
    const p = pool.find((x) => x.network === n.id);
    return [`${n.id} (${n.chain})`, p ? String(p.free) : '—', p ? String(p.assigned) : '—'];
});
/** Yield plans: [plan, yearly rate, smallest stake, most per client, offered]. */
export function guideYieldRows(plans) {
    if (!plans.length)
        return [['No plan is set up', '—', '—', '—', '—']];
    return plans.map((p) => [`${p.coin} ${termLabel(p.termDays)}`, `${D.from(p.apy).toString()}%`, usd(p.minStake), usd(p.maxPerUser), p.enabled ? 'Yes' : 'No']);
}
/** Earn tiers: [tier, live volume to reach it, fee cashback]. */
export const guideTierRows = (tiers) => tiers.map((t) => [t.name, usd(String(t.minVolume)), `${t.cashbackPct}%`]);
/** "+0.5% bonus, 1% early-exit fee" per Smart Portfolio lock term. */
export const guideLockTerms = (terms) => terms.map((t) => `${termLabel(t.days)}: +${t.bonusBps / 100}% bonus, ${t.exitFeeBps / 100}% fee to leave early`).join('; ');
/** "Direct up to $500 · approves up to $2,000", "Request", "—". */
export function guideMatrixCell(c, money) {
    if (!c)
        return '—';
    const parts = [];
    if (c.act === 'direct')
        parts.push(money && c.directLimit !== null ? `Direct up to ${usd(c.directLimit)}` : 'Direct');
    if (c.act === 'request')
        parts.push('Request');
    if (c.approve)
        parts.push(money && c.approveLimit !== null ? `approves up to ${usd(c.approveLimit)}` : 'approves');
    return parts.length ? parts.join(' · ').replace(/^approves/, 'Approves') : '—';
}
/** The permission matrix: [permission, then one cell per role in `roles`]. */
export function guideMatrixRows(permissions, roles, matrix) {
    return permissions.map((p) => [p.label, ...roles.map((r) => guideMatrixCell(matrix[r]?.find((x) => x.permission === p.key), p.money))]);
}
//# sourceMappingURL=vars.js.map